import type { ComponentType } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

import { Button } from '~/components/Button';

import { Modal } from '../Modal';

interface ModalProps {
  title: React.ReactNode;
  children: React.ReactNode;
  footer?: ComponentType<{ isLoading: boolean }>;
}

export interface ConfirmProps {
  title: React.ReactNode;
  message: string | React.ReactNode;
  confirmText: string;
  cancelText?: string;
  onCancel?: () => void;
  onConfirm: () => Promise<void> | void;
}

interface ModalContextType {
  confirm: (props: ConfirmProps) => void;
}

const ModalContext = createContext<ModalContextType>(
  null as unknown as ModalContextType,
);

ModalContext.displayName = 'ModalContext';

export interface ModalContextProviderProps {
  children: React.ReactNode;
}

export function ModalContextProvider({ children }: ModalContextProviderProps) {
  const [modalProps, setModalProps] = useState<ModalProps | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const confirm = useCallback(
    ({
      cancelText,
      confirmText,
      message,
      onConfirm,
      onCancel,
      title,
    }: ConfirmProps) => {
      setModalProps({
        title,
        children: message,
        footer: ({ isLoading: isLoadingScoped }) => (
          <>
            {cancelText?.length ? (
              <Button
                disabled={isLoadingScoped}
                variant="tertiary"
                onClick={() => {
                  onCancel?.();
                  setModalProps(null);
                }}
              >
                {cancelText}
              </Button>
            ) : null}
            <Button
              disabled={isLoadingScoped}
              variant="primary"
              onClick={async () => {
                setIsLoading(true);
                await onConfirm();
                setIsLoading(false);
                setModalProps(null);
              }}
            >
              {confirmText}
            </Button>
          </>
        ),
      });
    },
    [],
  );

  const Footer = modalProps?.footer ?? (() => null);

  return (
    <ModalContext.Provider value={{ confirm }}>
      <Modal
        isOpen={!!modalProps}
        onOpenChange={() => {
          if (!isLoading) {
            setModalProps(null);
          }
        }}
        title={modalProps?.title}
        footer={<Footer isLoading={isLoading} />}
        disableClose={isLoading}
      >
        {modalProps?.children}
      </Modal>
      {children}
    </ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

/**
 * This hook provides a function to open a confirmation modal.
 */
export const useModalContext = () => {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error(
      'useModalContext must be used within a ModalContextProvider',
    );
  }

  return context;
};
